import { useState } from 'react';
import { Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { trackJoinWaitlist, trackContactSales } from '@/lib/analytics-events';
import { submitWebsiteLead } from '@/api/leads';
import { leadProductGroupForScope } from '@/lib/lead-product-group';
import type { ServiceProduct } from './categories';

interface CatalogInquiryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: ServiceProduct;
  /** 'waitlist' for coming-soon products, 'sales' for available ones. */
  mode: 'waitlist' | 'sales';
}

/**
 * Lightweight inquiry form. Submits by composing a mailto to the sales inbox
 * (no waitlist endpoint exists), pre-filled with the product + the user's
 * account email so sales can reply directly.
 */
export function CatalogInquiryDialog({ open, onOpenChange, product, mode }: CatalogInquiryDialogProps) {
  const { user } = useAuth();
  const { show } = useToast();
  const waitlist = mode === 'waitlist';

  const template = waitlist
    ? `Hi team, I'd like to join the waitlist for ${product.displayName} and be notified when it launches.`
    : `Hi team, I'd like to learn more about ${product.displayName} and discuss provisioning it for my account.`;
  const [message, setMessage] = useState(template);
  const [submitting, setSubmitting] = useState(false);

  async function handleSend() {
    // Lead signal: join-waitlist (coming-soon) vs sales inquiry (available).
    if (waitlist) trackJoinWaitlist(product.displayName, product.scope);
    else trackContactSales(product.displayName, product.scope);
    // Persist the lead to DataCentral (same path as the other contact dialogs)
    // so the interested service reaches sales — not just a mailto that vanishes.
    // `interestedSolutions` carries the product so it lands on the lead's
    // interested-services field; the intent (waitlist vs sales) rides in the note.
    setSubmitting(true);
    try {
      await submitWebsiteLead({
        email: user?.email ?? '',
        // Send the canonical product-group so the lead's interested service maps
        // to a real CRM option; fall back to the display name if unmapped.
        interestedSolutions: [leadProductGroupForScope(product.scope) ?? product.displayName],
        specificNeeds:
          `${waitlist ? 'Waitlist request' : 'Sales inquiry'}: ${product.displayName}\n\n${message.trim()}`,
      });
      show({
        tone: 'success',
        title: waitlist ? 'Request received' : 'Sent to sales',
        description: "Our team will be in touch shortly.",
      });
      onOpenChange(false);
    } catch {
      show({ tone: 'danger', title: 'Could not send your request', description: 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{waitlist ? 'Join the waitlist' : 'Contact sales'}</DialogTitle>
          <DialogDescription>
            {product.displayName}
            {user?.email ? ` · we'll reply to ${user.email}` : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="catalog-inquiry-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            <Field label="Message">
              {(props) => (
                <Textarea
                  {...props}
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us a bit about your use case…"
                />
              )}
            </Field>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="catalog-inquiry-form"
            startIcon={<Mail size={16} />}
            disabled={submitting}
          >
            {submitting ? 'Sending…' : waitlist ? 'Request access' : 'Send to sales'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
